import { ContentCopy, DeleteOutline, Edit, Link as IconLink } from "@mui/icons-material";
import { Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import moment from "moment";
import { DocumentAction, DownloadDoc, onlyOfficePreviableTypes } from "../../components";
import { PublishedIcon } from "../../images/svg/publishedIcon";
import { WIPIcon } from "../../images/svg/WIPIcon";
import { AppRoutes } from "../../router/routes";

export const DashboardTableColumn = ({ onDelete, view, openAssociations, updatePublishStatus, forceRelease, updatable, deletable, handleDuplicate }: { onDelete: any, view: any, openAssociations: (row: any) => void, updatePublishStatus: (row: any) => void, forceRelease?: (row: any) => void, updatable: boolean, deletable: boolean, handleDuplicate?: (row: any) => void }) => {
    let columns = [
        {
            field: 'sequence_id', headerName: 'IEDOCS ID', width: 120, renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === 'group') {
                    return params.value;
                }
                return <Typography color="primary" sx={updatable ? { color: 'blue', textDecoration: "underline", cursor: "pointer" } : { textDecoration: "none" }} variant="body2" onClick={() => view(params.row)}>{`${params.row?.sequence_id}-r${params.row?.version}`}</Typography >
            }
        },
        {
            field: 'actions',
            // type: 'actions',
            headerName: 'Actions',
            width: 150,
            cellClassName: 'actions',
            sortable: false,
            hideSortIcons: true,
            disableColumnMenu: true,
            resizable: false,
            renderCell: (params: any) => {
                const fileType = params.row?.s3key?.split('.')?.pop();
                const actions = [
                    <DownloadDoc preview={true} id={params.row._id} url={params.row.url} status={params.row.status} fileType={fileType ?? "Unknown"} />,
                    <Tooltip title="Make a Duplicate">
                        <IconButton
                            // sx={{ margin: "auto" }}
                            onClick={() =>
                                handleDuplicate && handleDuplicate(params?.row)
                            }
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ];

                if (updatable) {
                    actions.unshift(
                        <DocumentAction
                            id={params.row._id}
                            status={params.row.status}
                            children={
                                <Tooltip title={params?.row?.released ? `${params?.row?.sequence_id} Revision is published` : `Edit the Document ${params?.row?.sequence_id}`}>
                                    <span>
                                        <IconButton
                                            color="primary"
                                            onClick={() => window.open(`${AppRoutes.officeEditor}?document_id=${params.row._id}`)}
                                            disabled={![...onlyOfficePreviableTypes, 'pdf'].includes(fileType) || params?.row?.released}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            }
                        />
                    )
                }


                return <Stack direction={'row'}>
                    {actions}
                </Stack>
            }
        },
        {
            field: 'file_name', headerName: 'IEDOCS Name', width: 200, renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === 'group') {
                    return params.value;
                }
                return <Tooltip title={params.row.file_name}><Typography variant="body2" sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>{params.row.file_name}</Typography></Tooltip>
            }
        },
        {
            field: 'released',
            headerName: 'Status',
            width: 90,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    params?.row?.released
                        ? <Button disableRipple disableFocusRipple sx={{ cursor: "default", '&:hover': { backgroundColor: 'transparent' } }} id="listView-published">
                            <Tooltip
                                title="Published"
                                children={<div><PublishedIcon /></div>}
                            />
                        </Button>
                        : <Button disableRipple disableFocusRipple>
                            <Tooltip title="Work in progress, click to publish the item" children={
                                <div><WIPIcon onClick={() => updatePublishStatus(params.row)} /></div>
                            } />
                        </Button>
                )
            }
        },
        {
            field: 'serial_num',
            headerName: 'Sequence Number',
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                return <Typography variant="body2" >{params?.row?.serial_num ?? ""}</Typography>
            }
        },
        {
            field: 'link', headerName: 'Dependencies', width: 140, renderCell: (params: GridRenderCellParams) => {
                return (
                    <IconButton onClick={() => openAssociations(params.row)}>
                        <IconLink color={"info"} />
                    </IconButton>
                )
            },
            disableColumnMenu: true,
            sortable: false,
        },
        { field: 'created_user', headerName: 'Uploaded By', width: 150 },
        {
            field: 'created_time', headerName: 'Uploaded Date', width: 250, renderCell: (params: GridRenderCellParams) => {
                return <Typography variant="body2">{moment(params.row.created_time).format('lll')}</Typography>
            }
        },
        { field: 'modified_user', headerName: 'Last modified by', width: 150 },
        {
            field: 'modified_time', headerName: 'Last modified date', width: 250, renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === 'group') {
                    return params.value;
                }
                return <Typography variant="body2">{params.row.modified_time ? moment(params.row.modified_time).format('lll') : ""}</Typography>
            }
        }
    ];

    if (updatable) {
        columns = [...columns.slice(0, 4), {
            field: 'download', headerName: 'Download', width: 100, renderCell: (params: GridRenderCellParams) => {
                return <DownloadDoc id={params.row._id} url={params.row.url} status={params.row.status} />
            },
            disableColumnMenu: true,
            sortable: false,
        }, ...columns.slice(4)]
    }

    if (deletable) {
        columns = [...columns, {
            field: 'delete', headerName: 'Delete', width: 100, renderCell: (params: GridRenderCellParams) => {
                return <IconButton onClick={() => onDelete(params.row)}>
                    <DeleteOutline color="error" />
                </IconButton>
            },
            disableColumnMenu: true,
            sortable: false,
        }]
    }

    if (!handleDuplicate) {
        columns.splice(2, 1);
    }

    return columns;
};

export const getEmtpyTextFile = async () => {
    const blob = new Blob([''], { type: 'text/txt' });
    return blob;
}